import { useState } from "react";
import { KeyRound, Save, Loader2, ArrowLeft } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useTranslation } from "react-i18next";

export default function ChangePassword() {
  const { user } = useAuth();
  const nav = useNavigate();
  const { t } = useTranslation();
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [saving, setSaving] = useState(false);

  if (!user) {
    return (
      <div className="max-w-md mx-auto p-6 text-center text-sm text-ink-textDim" data-testid="change-pw-signin-cta">
        <Link to="/login" className="text-brand-accent font-black underline">{t("nav.signIn", "Sign in")}</Link> to change your password.
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (newPw.length < 8) return toast.error(t("auth.pwTooShort", "Password must be at least 8 characters"));
    if (newPw !== confirmPw) return toast.error(t("auth.pwMismatch", "Passwords do not match"));
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: currentPw, new_password: newPw });
      toast.success("Password updated");
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
      // Force fresh session
      setTimeout(() => nav("/account", { replace: true }), 400);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-6" data-testid="change-password-page">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-xs text-ink-textDim mb-3" data-testid="change-pw-back">
        <ArrowLeft size={12} /> Back
      </button>
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-2xl bg-brand-accent/15 text-brand-accent flex items-center justify-center border border-brand-accent/30">
          <KeyRound size={16} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest font-black text-ink-textDim">// SECURITY</div>
          <h1 className="text-2xl font-black leading-tight">Change Password</h1>
        </div>
      </div>
      <p className="mt-2 text-sm text-ink-textDim">
        Choose a strong password (at least 8 characters). You'll stay logged in on this device.
      </p>

      <form onSubmit={submit} className="mt-5 bg-ink-surface border border-ink-border rounded-2xl p-4 space-y-3">
        <div>
          <label className="block text-[10px] font-black uppercase tracking-widest text-ink-textDim mb-1">{t("auth.currentPassword", "Current password")}</label>
          <input
            type="password"
            value={currentPw}
            onChange={(e) => setCurrentPw(e.target.value)}
            className="w-full bg-ink-bg border border-ink-border rounded-xl px-3 py-2 text-sm outline-none focus:border-brand-accent"
            required autoComplete="current-password"
            data-testid="change-pw-current"
          />
        </div>
        <div>
          <label className="block text-[10px] font-black uppercase tracking-widest text-ink-textDim mb-1">{t("auth.newPassword", "New password")}</label>
          <input
            type="password"
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            className="w-full bg-ink-bg border border-ink-border rounded-xl px-3 py-2 text-sm outline-none focus:border-brand-accent"
            required minLength={8} autoComplete="new-password"
            data-testid="change-pw-new"
          />
        </div>
        <div>
          <label className="block text-[10px] font-black uppercase tracking-widest text-ink-textDim mb-1">{t("auth.confirmPassword", "Confirm new password")}</label>
          <input
            type="password"
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
            className="w-full bg-ink-bg border border-ink-border rounded-xl px-3 py-2 text-sm outline-none focus:border-brand-accent"
            required minLength={8} autoComplete="new-password"
            data-testid="change-pw-confirm"
          />
        </div>
        <button
          type="submit"
          disabled={saving || !currentPw || !newPw || !confirmPw}
          className="w-full bg-brand-accent hover:bg-brand-accent/90 text-white rounded-full py-2.5 text-sm font-black inline-flex items-center justify-center gap-2 disabled:opacity-40"
          data-testid="change-pw-submit"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Update Password
        </button>
      </form>
    </div>
  );
}
